import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Eye, Flame, Radio } from "lucide-react";
import { api, media, fmtDate } from "@/lib/api";
import { ArtistCard } from "@/components/fo/ArtistCard";
import { Cover, SectionHead } from "@/components/fo/Bits";

const Podium = ({ title, rows, kind }) => (
  <div className="panel p-6" data-testid={`weekly-podium-${kind}`}>
    <div className="label-mono mb-4">{title}</div>
    {rows.length ? rows.slice(0, 3).map((r) => (
      <Link key={r.track_id} to={`/a/${r.artist.slug}`} className="flex items-center gap-4 border-b border-white/5 py-3 hover:text-[#00F5D4]">
        <span className={`led text-2xl font-bold ${r.rank === 1 ? "!text-[#FF2E63]" : ""}`}>0{r.rank}</span>
        <Cover src={r.cover} className="h-11 w-11 rounded-md" />
        <div className="min-w-0 flex-1"><div className="truncate font-display font-bold">{r.title}</div><div className="truncate text-sm text-slate-400">{r.artist.stage_name}</div></div>
        <span className="flex items-center gap-1 font-mono text-sm text-[#FF2E63]"><Flame className="h-4 w-4" />{r.flames}</span>
      </Link>
    )) : <p className="text-sm text-slate-500">No chart data for last week yet.</p>}
  </div>
);

export default function Weekly() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/weekly").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="pt-40 text-center font-mono text-slate-500">Loading…</div>;
  const w = d.blind_winner;
  return (
    <div className="mx-auto max-w-6xl px-4 pb-32 pt-28 md:px-8" data-testid="weekly-page">
      <SectionHead label={`Week ${fmtDate(d.week_start)} → ${fmtDate(d.week_end)}`} title="The Weekly Recap">
        Last week on Fuorionda in one page: both charts, the Blind Drop winner and the new artists who just went on air.
      </SectionHead>
      <div className="mb-8 flex flex-wrap gap-6 font-mono text-sm text-slate-400">
        <span className="flex items-center gap-2"><Radio className="h-4 w-4 text-[#00F5D4]" /><span className="led">{d.totals.plays}</span> plays</span>
        <span className="flex items-center gap-2"><Flame className="h-4 w-4 text-[#FF2E63]" /><span className="led">{d.totals.flames}</span> Flames</span>
      </div>
      {w && (
        <Link to={`/a/${w.artist.slug}`} className="panel console-corners relative mb-8 grid items-center gap-6 overflow-hidden p-6 md:grid-cols-[200px_1fr] md:p-8" data-testid="weekly-blind-winner">
          <Cover src={w.cover} className="aspect-square w-full rounded-2xl" />
          <div>
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#FFB800]"><Eye className="h-4 w-4" />Blind Drop winner</div>
            <h2 className="mt-3 font-display text-4xl font-black uppercase leading-none sm:text-5xl">{w.artist.stage_name}</h2>
            <p className="mt-2 text-lg text-slate-300">"{w.title}" · {w.flames} blind Flames from strangers</p>
          </div>
        </Link>
      )}
      {d.artist_of_month && (
        <Link to={`/a/${d.artist_of_month.slug}`} className="panel mb-8 flex items-center gap-5 border-[#FFB800]/40 p-6" data-testid="weekly-aotm">
          <Crown className="h-8 w-8 text-[#FFB800]" />
          <div><div className="label-mono !text-[#FFB800]">Artist of the Month · {d.artist_of_month.label}</div><div className="font-display text-2xl font-bold">{d.artist_of_month.stage_name}</div></div>
        </Link>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <Podium title="Blind Chart — top 3" rows={d.blind} kind="blind" />
        <Podium title="Fan Chart — top 3" rows={d.fan} kind="fan" />
      </div>
      {d.new_artists.length > 0 && (
        <div className="mt-16">
          <SectionHead label="Fresh on air" title="New artists" />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{d.new_artists.map((a, i) => <ArtistCard key={a.id} a={a} i={i} />)}</div>
        </div>
      )}
      <div className="mt-16 flex flex-wrap gap-4">
        <Link to="/radio" className="btn-hot" data-testid="weekly-listen">Listen live</Link>
        <Link to="/charts" className="btn-ghost" data-testid="weekly-charts">This week's chart</Link>
      </div>
    </div>
  );
}
